import React, { useState, useEffect } from 'react';
import { api } from '../services/authService';
import toast from 'react-hot-toast';
import {
  Settings,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  AlertTriangle,
  Wrench,
  Truck,
  Info,
  Copy,
  ArrowRight
} from 'lucide-react';

const MaintenanceConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [filterVehicleType, setFilterVehicleType] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Estados para cópia em lote
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySourceType, setCopySourceType] = useState('truck');
  const [copyTargetType, setCopyTargetType] = useState('excavator');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [copying, setCopying] = useState(false);

  const [formData, setFormData] = useState({
    vehicle_type: 'truck',
    component_category: 'engine',
    component_name: '',
    description: '',
    maintenance_interval_km: '',
    maintenance_interval_hours: '',
    maintenance_interval_days: '',
    warning_threshold_percent: 80,
    critical_threshold_percent: 95,
    is_active: true
  });

  const vehicleTypes = [
    { value: 'truck', label: 'Caminhão' },
    { value: 'excavator', label: 'Escavadeira' },
    { value: 'tractor', label: 'Trator' },
    { value: 'loader', label: 'Carregadeira' },
    { value: 'other', label: 'Outro' }
  ];

  const componentCategories = [
    { value: 'engine', label: 'Motor' },
    { value: 'transmission', label: 'Transmissão' },
    { value: 'brakes', label: 'Freios' },
    { value: 'suspension', label: 'Suspensão' },
    { value: 'electrical', label: 'Elétrica' },
    { value: 'hydraulic', label: 'Hidráulica' },
    { value: 'tires', label: 'Pneus' },
    { value: 'other', label: 'Outro' }
  ];

  useEffect(() => {
    loadConfigs();
  }, [filterVehicleType]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const params = filterVehicleType ? `?vehicle_type=${filterVehicleType}` : '';
      const response = await api.get(`/api/predictive-maintenance/component-configs${params}`);
      setConfigs(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        vehicle_type: config.vehicle_type,
        component_category: config.component_category,
        component_name: config.component_name,
        description: config.description || '',
        maintenance_interval_km: config.maintenance_interval_km || '',
        maintenance_interval_hours: config.maintenance_interval_hours || '',
        maintenance_interval_days: config.maintenance_interval_days || '',
        warning_threshold_percent: config.warning_threshold_percent,
        critical_threshold_percent: config.critical_threshold_percent,
        is_active: config.is_active
      });
    } else {
      setEditingConfig(null);
      setFormData({
        vehicle_type: 'truck',
        component_category: 'engine',
        component_name: '',
        description: '',
        maintenance_interval_km: '',
        maintenance_interval_hours: '',
        maintenance_interval_days: '',
        warning_threshold_percent: 80,
        critical_threshold_percent: 95,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConfig(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação: pelo menos um intervalo deve ser preenchido
    if (!formData.maintenance_interval_km &&
        !formData.maintenance_interval_hours &&
        !formData.maintenance_interval_days) {
      toast.error('Informe pelo menos um intervalo de manutenção (KM, Horas ou Dias)');
      return;
    }

    try {
      const payload = {
        ...formData,
        maintenance_interval_km: formData.maintenance_interval_km ? parseInt(formData.maintenance_interval_km) : null,
        maintenance_interval_hours: formData.maintenance_interval_hours ? parseInt(formData.maintenance_interval_hours) : null,
        maintenance_interval_days: formData.maintenance_interval_days ? parseInt(formData.maintenance_interval_days) : null,
        warning_threshold_percent: parseInt(formData.warning_threshold_percent),
        critical_threshold_percent: parseInt(formData.critical_threshold_percent)
      };

      if (editingConfig) {
        await api.put(`/api/predictive-maintenance/component-configs/${editingConfig.id}`, payload);
        toast.success('Configuração atualizada com sucesso');
      } else {
        await api.post('/api/predictive-maintenance/component-configs', payload);
        toast.success('Configuração criada com sucesso');
      }

      handleCloseModal();
      loadConfigs();
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar configuração');
    }
  };

  const handleDelete = async (id) => {
    if (deleteConfirmId !== id) {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
      return;
    }

    try {
      await api.delete(`/api/predictive-maintenance/component-configs/${id}`);
      toast.success('Configuração excluída com sucesso');
      loadConfigs();
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Erro ao excluir configuração:', error);
      toast.error('Erro ao excluir configuração');
    }
  };

  const handleBulkCopy = async () => {
    if (copySourceType === copyTargetType) {
      toast.error('Selecione tipos diferentes para origem e destino');
      return;
    }

    try {
      setCopying(true);

      const response = await api.post('/api/predictive-maintenance/component-configs/bulk-copy', {
        source_vehicle_type: copySourceType,
        target_vehicle_type: copyTargetType,
        overwrite_existing: overwriteExisting
      });

      toast.success(response.data.message);
      setShowCopyModal(false);
      loadConfigs();

      // Reset estados
      setCopySourceType('truck');
      setCopyTargetType('excavator');
      setOverwriteExisting(false);

    } catch (error) {
      console.error('Erro ao copiar configurações:', error);
      toast.error(error.response?.data?.message || 'Erro ao copiar configurações');
    } finally {
      setCopying(false);
    }
  };

  const getVehicleTypeLabel = (type) => {
    return vehicleTypes.find(v => v.value === type)?.label || type;
  };

  const getCategoryLabel = (category) => {
    return componentCategories.find(c => c.value === category)?.label || category;
  };

  const formatInterval = (config) => {
    const intervals = [];
    if (config.maintenance_interval_km) {
      intervals.push(`${config.maintenance_interval_km.toLocaleString('pt-BR')} km`);
    }
    if (config.maintenance_interval_hours) {
      intervals.push(`${config.maintenance_interval_hours}h`);
    }
    if (config.maintenance_interval_days) {
      intervals.push(`${config.maintenance_interval_days} dias`);
    }
    return intervals.join(' ou ');
  };

  // Agrupar configurações por tipo de veículo
  const groupedConfigs = configs.reduce((acc, config) => {
    const type = config.vehicle_type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(config);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500 rounded-lg">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Configurações de Manutenção
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Gerencie os intervalos de manutenção por tipo de veículo
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCopyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
            >
              <Copy className="w-5 h-5" />
              Copiar em Lote
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nova Configuração
            </button>
          </div>
        </div>

        {/* Filtro */}
        <div className="mt-4 flex items-center gap-4">
          <label className="text-gray-700 dark:text-gray-300 font-medium">
            Filtrar por tipo:
          </label>
          <select
            value={filterVehicleType}
            onChange={(e) => setFilterVehicleType(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="">Todos os tipos</option>
            {vehicleTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <span className="text-gray-600 dark:text-gray-400">
            {configs.length} configuração(ões) encontrada(s)
          </span>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Lista Agrupada */}
          {Object.keys(groupedConfigs).length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg p-12 text-center">
              <Wrench className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Nenhuma configuração encontrada
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Comece criando uma nova configuração de manutenção
              </p>
              <button
                onClick={() => handleOpenModal()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Criar Primeira Configuração
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedConfigs).map(([vehicleType, typeConfigs]) => (
                <div key={vehicleType} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                  {/* Header do grupo */}
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Truck className="w-6 h-6 text-white" />
                      <h2 className="text-xl font-bold text-white">
                        {getVehicleTypeLabel(vehicleType)}
                      </h2>
                      <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm">
                        {typeConfigs.length} configuração(ões)
                      </span>
                    </div>
                  </div>

                  {/* Tabela */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Categoria
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Componente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Intervalo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Alertas
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {typeConfigs.map(config => (
                          <tr key={config.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm">
                                {getCategoryLabel(config.component_category)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {config.component_name}
                              </div>
                              {config.description && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {config.description}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {formatInterval(config)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                                <span className="text-gray-700 dark:text-gray-300">{config.warning_threshold_percent}%</span>
                                <span className="text-gray-400">|</span>
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                <span className="text-gray-700 dark:text-gray-300">{config.critical_threshold_percent}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                config.is_active
                                  ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                              }`}>
                                {config.is_active ? 'Ativo' : 'Inativo'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleOpenModal(config)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                  title="Editar"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(config.id)}
                                  className={`${
                                    deleteConfirmId === config.id
                                      ? 'text-red-600 dark:text-red-400 font-bold'
                                      : 'text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400'
                                  }`}
                                  title={deleteConfirmId === config.id ? 'Clique novamente para confirmar' : 'Excluir'}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingConfig ? 'Editar Configuração' : 'Nova Configuração'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Tipo de Veículo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tipo de Veículo *
                </label>
                <select
                  value={formData.vehicle_type}
                  onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                  disabled={!!editingConfig}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                  required
                >
                  {vehicleTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Categoria do Componente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Categoria do Componente *
                </label>
                <select
                  value={formData.component_category}
                  onChange={(e) => setFormData({ ...formData, component_category: e.target.value })}
                  disabled={!!editingConfig}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-900"
                  required
                >
                  {componentCategories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Nome do Componente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome do Componente *
                </label>
                <input
                  type="text"
                  value={formData.component_name}
                  onChange={(e) => setFormData({ ...formData, component_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: Óleo do Motor, Filtro de Ar"
                  required
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="2"
                  placeholder="Descrição opcional da manutenção"
                />
              </div>

              {/* Intervalos de Manutenção */}
              <div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-2 mb-3">
                  <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Informe pelo menos um intervalo de manutenção (KM, Horas ou Dias)
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Intervalo em KM
                    </label>
                    <input
                      type="number"
                      value={formData.maintenance_interval_km}
                      onChange={(e) => setFormData({ ...formData, maintenance_interval_km: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ex: 10000"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Intervalo em Horas
                    </label>
                    <input
                      type="number"
                      value={formData.maintenance_interval_hours}
                      onChange={(e) => setFormData({ ...formData, maintenance_interval_hours: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ex: 250"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Intervalo em Dias
                    </label>
                    <input
                      type="number"
                      value={formData.maintenance_interval_days}
                      onChange={(e) => setFormData({ ...formData, maintenance_interval_days: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ex: 365"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Thresholds */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Alerta de Aviso (%)
                  </label>
                  <input
                    type="number"
                    value={formData.warning_threshold_percent}
                    onChange={(e) => setFormData({ ...formData, warning_threshold_percent: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="0"
                    max="100"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Alerta amarelo quando atingir essa porcentagem
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Alerta Crítico (%)
                  </label>
                  <input
                    type="number"
                    value={formData.critical_threshold_percent}
                    onChange={(e) => setFormData({ ...formData, critical_threshold_percent: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="0"
                    max="100"
                    required
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Alerta vermelho quando atingir essa porcentagem
                  </p>
                </div>
              </div>

              {/* Status Ativo */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Configuração ativa
                </label>
              </div>

              {/* Botões */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Save className="w-4 h-4" />
                  {editingConfig ? 'Atualizar' : 'Criar'} Configuração
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Cópia em Lote */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4 flex items-center justify-between rounded-t-lg">
              <div className="flex items-center gap-3">
                <Copy className="w-6 h-6 text-white" />
                <h3 className="text-xl font-bold text-white">
                  Copiar Configurações em Lote
                </h3>
              </div>
              <button
                onClick={() => setShowCopyModal(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Info box */}
              <div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    <p className="font-semibold mb-1">Como funciona:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Copie todas as configurações de um tipo de veículo para outro</li>
                      <li>As configurações inativas também serão copiadas</li>
                      <li>Se já existirem configurações no destino, você pode escolher sobrescrevê-las</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Seleção de tipos */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Copiar DE (origem):
                  </label>
                  <select
                    value={copySourceType}
                    onChange={(e) => setCopySourceType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {vehicleTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Seta visual */}
                <div className="flex justify-center">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900 dark:bg-opacity-30 rounded-full">
                    <ArrowRight className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Copiar PARA (destino):
                  </label>
                  <select
                    value={copyTargetType}
                    onChange={(e) => setCopyTargetType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {vehicleTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Opção de sobrescrever */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="overwrite_existing"
                    checked={overwriteExisting}
                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                    className="w-4 h-4 mt-1 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <div>
                    <label htmlFor="overwrite_existing" className="font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                      Sobrescrever configurações existentes
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Se marcado, as configurações que já existem no destino serão atualizadas.
                      Se desmarcado, configurações existentes serão mantidas e apenas novas serão adicionadas.
                    </p>
                  </div>
                </div>
              </div>

              {/* Preview de quantidade */}
              {copySourceType && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">
                      Configurações em <strong>{getVehicleTypeLabel(copySourceType)}</strong>:
                    </span>
                    <span className="font-bold text-lg text-purple-600 dark:text-purple-400">
                      {configs.filter(c => c.vehicle_type === copySourceType).length}
                    </span>
                  </div>
                </div>
              )}

              {/* Botões */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowCopyModal(false)}
                  disabled={copying}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkCopy}
                  disabled={copying || copySourceType === copyTargetType}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {copying ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Copiando...
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copiar Configurações
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceConfigPage;
